import { useCallback, useEffect, useRef, useState } from "react";
import { BookingStatus, RegisterButtonLocation } from "@repo/database";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { isSameDay } from "date-fns";
import { CalendarIcon, PlusIcon, SearchIcon } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

import BookingSearch from "~/components/booking/booking-search";
import LayoutCalendar from "~/components/booking/layout-calendar";
import { FunctionButtons } from "~/components/button/function-buttons";
import { DateNav } from "~/components/miscellaneous/date-nav";
import { Button } from "~/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { useBooking } from "~/hooks/use-booking";
import { useButton } from "~/hooks/use-button";
import { useMediaQuery } from "~/hooks/use-media-query";
import { useShift } from "~/hooks/use-shift";
import { api } from "~/lib/api";
import { cn, getMatchedShifts } from "~/lib/utils";
import { useBookingManager } from "~/managers/use-booking-manager";
import {
  useBookingStore,
  useTempBookingStore,
} from "~/stores/use-booking-store";
import { useShiftStore } from "~/stores/use-shift-store";

export const Route = createFileRoute("/_main/bookings/_layout")({
  component: BookingLayout,
});

function BookingLayout() {
  const { t } = useTranslation("common");

  const [bookingSearchOpen, setBookingSearchOpen] = useState(false);

  const { selectedDate, setSelectedDate } = useTempBookingStore();

  const { layoutCalendarOpen, setLayoutCalendarOpen } = useBookingStore();

  const { openPendingBookings, openBookingDialog } = useBookingManager();

  const { buttons } = useButton();

  const isMobile = useMediaQuery(768);

  const bookingButtons = buttons.filter(
    (button) =>
      button.location === RegisterButtonLocation.calendar_footer &&
      button.mobile === isMobile,
  );

  const { data: day } = api.shift.getDay.useQuery(selectedDate, {
    staleTime: 15000,
  });

  const { shifts } = useShift();

  const {
    setSelectedDate: setShiftStatusSelectedDate,
    setShiftStatusFormOpen,
  } = useShiftStore();

  const { bookings, fetchBookings } = useBooking();

  const dayBookings = bookings.filter(
    (booking) =>
      booking.bookingStatus !== BookingStatus.canceled &&
      isSameDay(booking.startTime, selectedDate) &&
      booking.groupSize > 0,
  );

  const reservations = dayBookings.length;

  const guests = dayBookings.reduce(
    (totalGuests, booking) => totalGuests + booking.groupSize,
    0,
  );

  const pendingBookings = bookings.filter(
    (booking) => booking.bookingStatus === BookingStatus.pending,
  );

  const tables = dayBookings.reduce(
    (totalTables, booking) => totalTables + booking.tableIds.length,
    0,
  );

  const matchedShifts = getMatchedShifts(shifts, selectedDate);

  const hasTempSlot =
    day
      ?.flatMap((shift) => shift.timeslots)
      .some((timeslot) => timeslot.temporary) ?? matchedShifts.length === 0;

  const isClosed =
    day?.some((shift) => shift.closed) ?? matchedShifts.length === 0;

  const calendarRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setLayoutCalendarOpen(false);
      }
    },
    [setLayoutCalendarOpen],
  );

  useEffect(() => {
    if (layoutCalendarOpen && isMobile) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside, layoutCalendarOpen, isMobile]);

  useEffect(() => {
    void fetchBookings();
  }, [fetchBookings]);

  return (
    <div className={cn("flex flex-col overflow-auto w-full flex-1")}>
      {pendingBookings.length > 0 && (
        <button
          onClick={() => {
            openPendingBookings();
          }}
          className="bg-yellow-500 text-black font-bold text-xl text-center p-4 animate-pulse"
        >
          {pendingBookings.length > 1
            ? t("Booking.x_pending_bookings", {
                x: pendingBookings.length,
              }).toUpperCase()
            : t("Booking.pending_booking").toUpperCase()}
        </button>
      )}
      <div className="flex flex-col flex-1 overflow-auto">
        <div className="flex flex-row flex-1 overflow-hidden relative">
          <div
            className={cn(
              "overflow-hidden w-full flex flex-col",
              layoutCalendarOpen && !isMobile && "pr-[276px]",
            )}
          >
            <div className="flex flex-row items-center justify-between gap-6 border-b px-4 py-1">
              <div className="text-xs text-muted-foreground w-[350px] flex flex-row items-center gap-2">
                <div>
                  {isMobile && (
                    <Trans
                      key="reservations"
                      i18nKey="Booking.reservations"
                      values={{ reservations, guests, tables }}
                      components={{
                        strong: <span key="strong" className="font-bold" />,
                      }}
                    />
                  )}
                  {!isMobile && (
                    <Trans
                      key="reservations_with_tables"
                      i18nKey="Booking.reservations_with_tables"
                      values={{ reservations, guests, tables }}
                      components={{
                        strong: <span key="strong" className="font-bold" />,
                      }}
                    />
                  )}
                </div>
              </div>

              <div className="md:flex hidden items-center justify-center flex-1">
                <DateNav
                  futureDates={true}
                  date={selectedDate}
                  onNavigate={(nextDate) => setSelectedDate(nextDate)}
                />
              </div>
              <div className="flex flex-row items-center gap-2 w-[300px] justify-end">
                <Button
                  onClick={() => {
                    setShiftStatusFormOpen(true);
                    setShiftStatusSelectedDate(selectedDate);
                  }}
                  className="p-1"
                  variant="ghost"
                >
                  <div
                    className={cn(
                      "before:absolute bg-green-500 w-2 h-2 rounded-full left-0 mr-2",
                      hasTempSlot && "bg-yellow-500",
                      isClosed && "bg-red-500",
                    )}
                  ></div>
                  {isClosed ? t("closed") : t("open")}
                </Button>
                <Button
                  onClick={() => {
                    setLayoutCalendarOpen(!layoutCalendarOpen);
                  }}
                  variant="ghost"
                  className="p-2 rounded-full"
                >
                  <CalendarIcon />
                </Button>
                <Popover
                  open={bookingSearchOpen}
                  onOpenChange={(open) => setBookingSearchOpen(open)}
                >
                  <PopoverTrigger asChild>
                    <Button variant="ghost" className="p-2 rounded-full">
                      <SearchIcon />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 min-w-[400px]">
                    <div className="overflow-auto max-h-[65dvh]">
                      <BookingSearch
                        onClose={() => setBookingSearchOpen(false)}
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="flex flex-col-reverse md:flex-col">
              {!isSameDay(selectedDate, new Date()) && (
                <div className="w-full h-10">
                  <Button
                    className={cn(
                      "text-sm p-1 h-10 animate-pulse w-full rounded-none -left-40",
                    )}
                    onClick={() => setSelectedDate(new Date())}
                    variant="warning"
                  >
                    {t("DateNav.today")}
                  </Button>
                </div>
              )}

              <div className="md:hidden flex items-center justify-center border-b">
                <DateNav
                  futureDates={true}
                  date={selectedDate}
                  onNavigate={(nextDate) => setSelectedDate(nextDate)}
                />
              </div>
            </div>

            <Outlet />
            {isMobile && layoutCalendarOpen && (
              <div className="absolute inset-0 bg-black/70 z-[99]"></div>
            )}
            <div></div>
          </div>
          <div
            ref={calendarRef}
            className={cn(
              "translate-x-full h-full transition-transform absolute w-[276px] z-[100] bg-background-secondary right-0 top-0",
              layoutCalendarOpen && "translate-x-0",
            )}
          >
            <LayoutCalendar
              onSelect={() => {
                if (isMobile) {
                  setLayoutCalendarOpen(false);
                }
              }}
            />
          </div>
        </div>
      </div>
      {bookingButtons.length === 0 && (
        <Button
          onClick={() => {
            void openBookingDialog();
          }}
          className="w-14 h-14 rounded-full items-center absolute right-4 bottom-4 z-[999]"
          variant="brand"
        >
          <PlusIcon />
        </Button>
      )}

      <FunctionButtons location={RegisterButtonLocation.calendar_footer} />
    </div>
  );
}
